import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Select, MenuItem, Tooltip } from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';

export const CHAT_MODE_KEY = 'chatMode';
export const CHAT_CLOUD_KEY = 'chatCloud';

// 'rag' answers from the indexed documents; the other modes answer from the user's own input
export const MODES = [
  {
    value: 'rag',
    label: 'Ask docs',
    icon: <MenuBookOutlinedIcon sx={{ fontSize: 18 }} />,
    tooltip: 'Answer from the indexed migration documents',
    placeholder: 'Ask your question...',
  },
  {
    value: 'review',
    label: 'Review & suggest',
    icon: <TipsAndUpdatesOutlinedIcon sx={{ fontSize: 18 }} />,
    tooltip: 'Paste requirements, an architecture or code and get enhancement suggestions',
    placeholder: 'Paste your requirements, architecture or code to review... (Shift+Enter for a new line)',
  },
  {
    value: 'terraform',
    label: 'Generate Terraform',
    icon: <CodeOutlinedIcon sx={{ fontSize: 18 }} />,
    tooltip: 'Describe your requirements and get a Terraform design and code',
    placeholder: 'Describe the infrastructure you need... (Shift+Enter for a new line)',
  },
  {
    value: 'opm',
    label: 'Ask OpManager',
    icon: <MonitorHeartOutlinedIcon sx={{ fontSize: 18 }} />,
    tooltip: 'Ask about alarms, servers, network devices and probes from live OpManager data (read-only)',
    placeholder: 'Ask about alarms, devices, servers, interfaces or probes...',
  },
];

export const CLOUDS = [
  { value: 'auto', label: 'Auto-detect cloud' },
  { value: 'azure', label: 'Azure' },
  { value: 'aws', label: 'AWS' },
  { value: 'gcp', label: 'GCP' },
];

export const getModeConfig = (mode) => MODES.find((m) => m.value === mode) || MODES[0];

export const readStoredMode = () => {
  try {
    const stored = localStorage.getItem(CHAT_MODE_KEY);
    return MODES.some((m) => m.value === stored) ? stored : 'rag';
  } catch (e) {
    return 'rag';
  }
};

export const readStoredCloud = () => {
  try {
    const stored = localStorage.getItem(CHAT_CLOUD_KEY);
    return CLOUDS.some((c) => c.value === stored) ? stored : 'auto';
  } catch (e) {
    return 'auto';
  }
};

const toggleSx = {
  fontFamily: 'Kumbh Sans',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: '#5b5f78',
  gap: '8px',
  padding: '8px 16px',
  border: 'none',
  borderRadius: '999px !important',
  transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#1a0652' },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: '#fff',
    color: '#1a0652',
    boxShadow: '0 1px 3px rgba(26, 6, 82, 0.12), 0 6px 16px rgba(26, 6, 82, 0.08)',
  },
  '&.Mui-selected .MuiSvgIcon-root': { color: '#6a4cff' },
};

// Segmented pill control for the three modes (+ cloud picker in Terraform mode)
const ModeSelector = ({ mode, onModeChange, cloud, onCloudChange, disabled }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 1.25 }}>
    <ToggleButtonGroup
      size="small"
      exclusive
      value={mode}
      disabled={disabled}
      onChange={(e, value) => value && onModeChange(value)}
      sx={{
        backgroundColor: '#f1effa',
        borderRadius: '999px',
        padding: '4px',
        gap: '4px',
        border: '1px solid #e7e4f5',
        '& .MuiToggleButtonGroup-grouped': { border: 'none', margin: 0 },
      }}
    >
      {MODES.map((m) => (
        <Tooltip key={m.value} title={m.tooltip} arrow describeChild>
          <ToggleButton value={m.value} sx={toggleSx}>
            {m.icon}
            {m.label}
          </ToggleButton>
        </Tooltip>
      ))}
    </ToggleButtonGroup>
    {mode === 'terraform' && (
      <Select
        size="small"
        value={cloud}
        disabled={disabled}
        onChange={(e) => onCloudChange(e.target.value)}
        sx={{
          fontFamily: 'Kumbh Sans',
          fontSize: '14px',
          fontWeight: 600,
          color: '#1a0652',
          height: '44px',
          borderRadius: '999px',
          backgroundColor: '#f1effa',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e7e4f5' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cfc8f3' },
          '& .MuiSelect-select': { paddingLeft: '18px' },
        }}
      >
        {CLOUDS.map((c) => (
          <MenuItem key={c.value} value={c.value} sx={{ fontFamily: 'Kumbh Sans', fontSize: '14px' }}>
            {c.label}
          </MenuItem>
        ))}
      </Select>
    )}
  </Box>
);

export default ModeSelector;
